import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { PlatformChatPanel } from "~/pages/manifold-nextgen/components/platform-help/PlatformChatPanel";
import { useCanUsePlatformAgent } from "~/pages/manifold-nextgen/hooks/useCanUsePlatformAgent";

interface ManifoldNextGenHelpPopoverProps {
  // See ManifoldNextGenNavPickerProps["colored"] — same colored-home-hero treatment.
  colored?: boolean;
}

export function ManifoldNextGenHelpPopover({ colored = false }: ManifoldNextGenHelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const enabled = useCanUsePlatformAgent();

  if (!enabled) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              aria-label="Ask AI"
              className={cn(
                "size-9 rounded-full p-0 text-muted-foreground duration-300 hover:bg-black/[0.06] hover:text-foreground focus-visible:ring-0",
                colored && "text-white/80 hover:bg-white/20 hover:text-white",
                open && (colored ? "bg-white/20 text-white" : "bg-black/[0.06] text-foreground")
              )}
            >
              <Sparkles className="size-5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {/* Hidden while open, so it doesn't cover the panel. */}
        {!open && <TooltipContent side="bottom">Ask AI</TooltipContent>}
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={10}
        // Focus the panel itself: its first control would open its own
        // tooltip, and focus left on the trigger would Tab out and dismiss it.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        className="relative z-[1300] w-[26rem] overflow-visible p-0 shadow-2xl"
      >
        {/* The arrow, centered under the trigger. */}
        <div
          aria-hidden
          className="absolute -top-1.5 right-3 size-3 rotate-45 rounded-[2px] border-t border-l border-border bg-popover"
        />
        <PlatformChatPanel />
      </PopoverContent>
    </Popover>
  );
}
